import { ShowHideButton } from "./ShowHideButton";
import { styles } from "./styles";
import { useTileFocusRestoration } from "./useTileFocusRestoration";
import { getTruncationContainerStyles, useTruncationStyles } from "./useTruncationStyles";
import {
  Box,
  Button,
  ChevronDown,
  ChevronUp,
  Flex,
  type BoxRef,
} from "../../../../../../../../mads";
import { useI18n } from "../../../../../../../../shared/I18n";
import { useAccessibilityFocus } from "../../../../../../hooks/useAccessibilityFocus";
import { useFullScreenVJActive } from "../../../../../../hooks/useFullScreenVJActive";
import { useLogging } from "../../../context/useLogging";
import { useShowCount } from "../../../utils/useShowCount";
import Tile, { type TileProps } from "../Tile";

import { colors } from "@indeed/ifl-tokens";
import React, { useEffect, useMemo, useRef, useState } from "react";

export interface TileListProps {
  tiles: TileProps[];
  section: string;
  isTruncatable?: boolean;
  visibleLines?: number;
}

interface ExpandButtonProps {
  handleClick(): void;
  hasInteractiveTiles: boolean;
  isExpanded: boolean;
  tilesCount: number;
}

const ExpandButton: React.FC<ExpandButtonProps> = ({
  handleClick,
  hasInteractiveTiles,
  isExpanded,
  tilesCount,
}) => {
  const initialShowCount = useShowCount(tilesCount);
  const renderExpandButton = tilesCount > initialShowCount;

  if (!renderExpandButton) {
    return null;
  }

  return (
    <Flex
      sx={[
        styles.listItemStyle,
        {
          display: "flex",
          flexGrow: hasInteractiveTiles ? 0 : 1,
          flexShrink: 0,
          flexBasis: hasInteractiveTiles ? "auto" : "100%",
        },
      ]}
    >
      <ShowHideButton
        isExpanded={isExpanded}
        onPress={handleClick}
        textSx={{
          color: colors.neutral[700],
          textDecorationLine: "underline",
          fontWeight: "normal",
        }}
      />
    </Flex>
  );
};

interface TruncationButtonProps {
  buttonVisibleStyles?: any;
  handleClick(): void;
  isExpanded: boolean;
}

const TruncationButton: React.FC<TruncationButtonProps> = ({ handleClick, isExpanded }) => {
  const { gettext } = useI18n();

  return (
    <Button
      variant="text"
      onPress={handleClick}
      size="md"
      iconAfter={isExpanded ? <ChevronUp /> : <ChevronDown />}
    >
      {isExpanded ? gettext("Show less") : gettext("Show more")}
    </Button>
  );
};
function getTilesLimit({
  tilesCount,
  initialShowCount,
  isExpanded,
  isTruncatable,
}: {
  tilesCount: number;
  initialShowCount: number;
  isExpanded: boolean;
  isTruncatable: boolean;
}) {
  if (isTruncatable) {
    return tilesCount;
  }

  if (tilesCount < initialShowCount) {
    return tilesCount;
  }

  return isExpanded ? tilesCount : initialShowCount;
}

export const TileList: React.FC<
  TileListProps & {
    children?: React.ReactNode;
  }
> = ({ tiles, section, isTruncatable = false, visibleLines = 2, children }) => {
  const logger = useLogging();
  const isFullScreenVJActive = useFullScreenVJActive();
  const initialShowCount = useShowCount(tiles.length);
  const [isExpanded, setIsExpanded] = useState(false);
  const limit = getTilesLimit({
    tilesCount: tiles.length,
    initialShowCount,
    isExpanded,
    isTruncatable,
  });
  const visibleTiles = useMemo(() => tiles.slice(0, limit), [tiles, limit]);
  const { tilesContainer } = useTruncationStyles(visibleTiles, visibleLines, isTruncatable);
  const truncationContainerStyles = getTruncationContainerStyles(
    isTruncatable,
    isExpanded,
    visibleLines,
  );
  const hasInteractiveTiles = tiles.some((tile) => !!tile.onPress);
  const showTruncationButton =
    isTruncatable && React.Children.toArray(children).filter(Boolean).length > 0;

  const focus = useAccessibilityFocus();
  const tileRefs = useRef<(BoxRef | null)[]>([]);
  const { handleTilePress } = useTileFocusRestoration({
    tiles,
    limit,
    focus,
    tileRefs,
  });

  useEffect(() => {
    const targetElement = tileRefs.current[initialShowCount];
    if (isExpanded && targetElement) {
      return focus(targetElement);
    }
  }, [isExpanded, focus, initialShowCount]);

  const handleClick = () => {
    if (!isExpanded) {
      setIsExpanded(true);
      logger.logListExpanded(true, section);
    } else {
      setIsExpanded(false);
      logger.logListExpanded(false, section);
    }
  };

  return (
    <Flex>
      <Flex sx={[styles.listBoxStyle, tilesContainer, truncationContainerStyles]}>
        {visibleTiles.map((tile, index) => (
          <Box id={tile.primaryText} sx={styles.listItemStyle} key={tile.primaryText}>
            <Tile
              {...tile}
              onPress={handleTilePress(tile, index)}
              ref={(ref) => {
                tileRefs.current[index] = ref;
              }}
            />
          </Box>
        ))}
        {!isTruncatable && !isFullScreenVJActive && (
          <ExpandButton
            isExpanded={isExpanded}
            handleClick={handleClick}
            hasInteractiveTiles={hasInteractiveTiles}
            tilesCount={tiles.length}
          />
        )}
      </Flex>
      {!isTruncatable && isFullScreenVJActive && (
        <Box sx={styles.expandButtonWrapper}>
          <ExpandButton
            isExpanded={isExpanded}
            handleClick={handleClick}
            hasInteractiveTiles={hasInteractiveTiles}
            tilesCount={tiles.length}
          />
        </Box>
      )}
      {showTruncationButton && (
        <TruncationButton handleClick={handleClick} isExpanded={isExpanded} />
      )}
      {isExpanded && children && <Flex sx={{ marginTop: 4 }}>{children}</Flex>}
    </Flex>
  );
};
